import { isAxiosError } from 'axios';

interface ApiErrorBody {
  message?: string;
  error?: string | null;
}

export class ApiRequestError extends Error {
  statusCode?: number;
  response?: {
    data?: ApiErrorBody;
    status?: number;
  };
  originalError?: unknown;

  constructor(message: string, options?: {
    statusCode?: number;
    response?: { data?: ApiErrorBody; status?: number };
    originalError?: unknown;
  }) {
    super(message);
    this.name = 'ApiRequestError';
    this.statusCode = options?.statusCode;
    this.response = options?.response;
    this.originalError = options?.originalError;
  }
}

export const extractApiErrorMessage = (
  error: unknown,
  fallback = 'Something went wrong.'
): string => {
  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  if (error instanceof ApiRequestError) {
    return error.message || fallback;
  }

  if (isAxiosError<ApiErrorBody>(error)) {
    return (
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      fallback
    );
  }

  if (error instanceof Error) {
    return error.message || fallback;
  }

  return fallback;
};

export const toApiRequestError = (
  error: unknown,
  fallback = 'Something went wrong.'
): ApiRequestError => {
  if (error instanceof ApiRequestError) {
    return error;
  }

  if (isAxiosError<ApiErrorBody>(error)) {
    return new ApiRequestError(extractApiErrorMessage(error, fallback), {
      statusCode: error.response?.status,
      response: error.response
        ? {
            data: error.response.data,
            status: error.response.status,
          }
        : undefined,
      originalError: error,
    });
  }

  return new ApiRequestError(extractApiErrorMessage(error, fallback), {
    originalError: error,
  });
};
